import React, { useState, useMemo } from 'react';
import './ProductsTableView.css';

function ProductsTableView({ products, onProductSelect, onDeleteProduct, onDuplicateProduct, onToggleOnline }) {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Sortierung Logic
  const sortedProducts = useMemo(() => {
    if (!products || products.length === 0) return products;
    if (!sortConfig) return products;
    
    const sorted = [...products].sort((a, b) => {
      let aValue = '';
      let bValue = '';

      switch (sortConfig.key) {
        case 'name':
          aValue = (a.name || '').toString().toLowerCase();
          bValue = (b.name || '').toString().toLowerCase();
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        case 'manufacturer':
          aValue = (a.manufacturerName || '').toString().toLowerCase();
          bValue = (b.manufacturerName || '').toString().toLowerCase();
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        case 'type':
          aValue = (a.productTypeName || '').toString().toLowerCase();
          bValue = (b.productTypeName || '').toString().toLowerCase();
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        case 'watts':
          aValue = Number(a.watts || 0);
          bValue = Number(b.watts || 0);
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        case 'bundleSize':
          aValue = extractBundleSizeValue(a);
          bValue = extractBundleSizeValue(b);
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        case 'prices':
          aValue = Number(a.priceCount || 0);
          bValue = Number(b.priceCount || 0);
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          return 0;
      }
    });

    return sorted;
  }, [products, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const getBundleSize = (product) => {
    // Zuerst versuchen, aus fertilizerSpecs zu nehmen
    if (product.fertilizerSpecs?.bottleSizeLiters) {
      return `${product.fertilizerSpecs.bottleSizeLiters} L`;
    }
    
    // Fallback: Aus dem Produktnamen extrahieren (z.B. "Bio Bizz Acti-Vera 1L")
    const nameMatch = product.name?.match(/(\d+(?:[.,]\d+)?)\s*[Ll]/);
    if (nameMatch) {
      return `${nameMatch[1]} L`;
    }
    
    return '-';
  };

  const extractBundleSizeValue = (product) => {
    // Für Sortierung: numerischen Wert extrahieren
    if (product.fertilizerSpecs?.bottleSizeLiters) {
      return product.fertilizerSpecs.bottleSizeLiters;
    }
    
    const nameMatch = product.name?.match(/(\d+(?:[.,]\d+)?)\s*[Ll]/);
    if (nameMatch) {
      return parseFloat(nameMatch[1].replace(',', '.'));
    }
    
    return 0;
  };

  return (
    <div className="products-table-view">
      <div className="table-wrapper">
        <table className="products-table">
          <colgroup>
            <col className="col-name" />
            <col className="col-manufacturer" />
            <col className="col-type" />
            <col className="col-watts" />
            <col className="col-bundle" />
            <col className="col-prices" />
            <col className="col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th 
                className="sortable"
                onClick={() => handleSort('name')}
              >
                Produktname{getSortIndicator('name')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('manufacturer')}
              >
                Hersteller{getSortIndicator('manufacturer')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('type')}
              >
                Produkttyp{getSortIndicator('type')}
              </th>
              <th 
                className="sortable numeric"
                onClick={() => handleSort('watts')}
              >
                Wattzahl{getSortIndicator('watts')}
              </th>
              <th 
                className="sortable numeric"
                onClick={() => handleSort('bundleSize')}
              >
                Gebindegröße{getSortIndicator('bundleSize')}
              </th>
              <th 
                className="sortable numeric"
                onClick={() => handleSort('prices')}
              >
                Preise Online{getSortIndicator('prices')}
              </th>
              <th className="actions">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {sortedProducts.map(product => (
              <tr 
                key={product._id}
                className={`product-row ${!product.online ? 'offline' : ''}`}
              >
                {/* Produktname */}
                <td className="product-name">
                  <div className="name-cell">
                    {product.image && (
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="product-thumb"
                      />
                    )}
                    <span className="name-text">{product.name}</span>
                  </div>
                </td>

                {/* Hersteller */}
                <td className="product-manufacturer">
                  {product.manufacturerName || '-'}
                </td>

                {/* Produkttyp */}
                <td className="product-type">
                  {product.productTypeName || '-'}
                </td>

                {/* Wattzahl */}
                <td className="product-watts">
                  {product.watts ? `${product.watts} W` : '-'}
                </td>

                {/* Gebindegröße */}
                <td className="product-bundle">
                  {getBundleSize(product)}
                </td>

                {/* Preise Online */}
                <td className="product-prices">
                  <span className={`price-badge ${product.priceCount > 0 ? 'has-prices' : 'no-prices'}`}>
                    {product.priceCount}
                  </span>
                </td>

                {/* Aktionen */}
                <td className="product-actions">
                  <button 
                    className={`action-btn status-btn ${product.online ? 'online' : 'offline'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleOnline(e, product._id);
                    }}
                    title={product.online ? 'Online - klicken zum deaktivieren' : 'Offline - klicken zum aktivieren'}
                  >
                    {product.online ? '🟢' : '🔴'}
                  </button>
                  <button 
                    className="action-btn duplicate-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicateProduct(e, product._id);
                    }}
                    title="Duplizieren"
                  >
                    📋
                  </button>
                  <button 
                    className="action-btn edit-btn"
                    onClick={() => onProductSelect(product._id)}
                    title="Bearbeiten"
                  >
                    ✏️
                  </button>
                  <button 
                    className="action-btn delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProduct(product._id);
                    }}
                    title="Löschen"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ProductsTableView;
